import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Potential", "Engaged", "Negotiation", "Converted", "Lost"];

export default function CustomerFormDialog({ open, onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    address: "",
    status: "Potential",
    notes: "",
    opportunity_value: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {

      // eslint-disable-next-line
      setForm({
        name: initial.name || "",
        company: initial.company || "",
        email: initial.email || "",
        phone: initial.phone || "",
        address: initial.address || "",
        status: initial.status || "Potential",
        notes: initial.notes || "",
        opportunity_value:
          initial.opportunity_value != null ? String(initial.opportunity_value) : "",
      });
    } else {
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        address: "",
        status: "Potential",
        notes: "",
        opportunity_value: "",
      });
    }
  }, [initial, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      ...form,
      opportunity_value:
        form.opportunity_value === "" ? 0 : parseFloat(form.opportunity_value) || 0,
    };
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await api.put(`/customers/${initial.id}`, payload);
      } else {
        res = await api.post("/customers", payload);
      }
      onSaved?.(res.data);
    } catch {
      toast.error("Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="sm:max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="customer-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Customer" : "New Customer"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isEdit
              ? "Update customer details and pipeline status."
              : "Add a new lead to your pipeline."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
            <Input
              data-testid="customer-form-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Full name"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Company</Label>
            <Input
              data-testid="customer-form-company"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="mt-1.5" data-testid="customer-form-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
            <Input
              data-testid="customer-form-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
            <Input
              data-testid="customer-form-phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Address</Label>
            <Input
              data-testid="customer-form-address"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Opportunity Value (₹)
            </Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                ₹
              </span>
              <Input
                data-testid="customer-form-opportunity"
                type="number"
                min="0"
                step="100"
                value={form.opportunity_value}
                onChange={(e) => update("opportunity_value", e.target.value)}
                placeholder="0"
                className="pl-7"
              />
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea
              data-testid="customer-form-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            💡 SVRAF PoCs are managed from the customer detail page after creation — you can add multiple PoCs and designate one as primary.
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="customer-form-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            data-testid="customer-form-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
